// NPM packages
import React from 'react';
import styled from '@emotion/styled';
import { MenuItem } from '@mui/material';
import { useLocation, matchPath } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';

// All other imports
import NavItem from 'types/NavItem';
import Banner from './Banner';
import HeaderNav from './HeaderNav';
import Link, { LinkProps } from 'components/core/Link';
import UserManagement from './UserManagement';
import { useServices } from '../../services';
import HoverMenu from 'components/core/HoverMenu';
import { observer } from 'mobx-react-lite';

const Root = styled.div({
  display: 'flex',
  flexDirection: 'column',
});

const PrimaryNav = styled.div(
  ({ theme: { outermostPadding, typography } }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    ...outermostPadding({
      vertical: {
        mobile: '0.75rem',
        tablet: typography.pxToRem(14.5),
        desktop: typography.pxToRem(20),
      },
    }),
  })
);

const Logo = styled.img(({ theme: { breakpoints } }) => ({
  width: '200px',
  [breakpoints.up('md')]: {
    width: '300px',
  },
  [breakpoints.up('lg')]: {
    width: '300px',
  },
  [breakpoints.up('xl')]: {
    width: '300px',
  },
}));

const RightHandSide = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
});

const SecondaryNav = styled(
  ({
    onHomepage,
    ...rest
  }: React.HTMLAttributes<HTMLDivElement> & { onHomepage: boolean }) => (
    <div {...rest} />
  )
)<{ onHomepage: boolean }>(
  ({ theme: { outermostPadding, typography, palette }, onHomepage }) => ({
    display: onHomepage ? 'none' : 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    backgroundColor: palette.primary.dark,
    gap: '3.35em',
    fontSize: '1.125rem',
    ...outermostPadding({
      vertical: {
        mobile: '0.75rem',
        tablet: typography.pxToRem(14.5),
        desktop: typography.pxToRem(20),
      },
    }),
    '& a': {
      color: 'white',
      textDecoration: 'none',
      '&:hover': {
        color: 'white',
        textDecoration: 'none',
      },
    },
  })
);

const SecondaryLink = styled(
  ({ onPage, ...rest }: LinkProps & { onPage: boolean }) => <Link {...rest} />
)(({ onPage }) => ({
  borderBottom: onPage ? '4px solid white' : 'none',
}));

export interface HeaderProps {
  className?: string;
  items: NavItem[];
}

function Header(props: HeaderProps): React.ReactElement | null {
  const { className, items } = props;
  const { envService } = useServices();
  const location = useLocation();
  const dataRepoURl = envService.getValue('DATA_REPO_URL');
  const onHomepage = Boolean(matchPath('/', location.pathname));
  const onComp = Boolean(
    matchPath('/computational-resources/*', location.pathname)
  );
  const onProj = Boolean(matchPath('/projects/*', location.pathname));
  const docsUrl = envService.getValue('DOCS_URL') ?? 'https://msd-live.github.io/msdlive-docs';

  return (
    <Root className={className}>
      <Banner />
      <PrimaryNav>
        <Link to="/" aria-label="homepage">
          <Logo src="/MSD.LIVE.horiz.svg" alt="" />
        </Link>
        <RightHandSide>
          <HeaderNav items={items} />
          <UserManagement />
        </RightHandSide>
      </PrimaryNav>
      <SecondaryNav onHomepage={onHomepage}>
        <HoverMenu
          renderAnchor={(props) => (
            <Link {...props} to={dataRepoURl} aria-label="Data Repository">
              Data Repository
            </Link>
          )}
          renderMenuItems={({ close }) => [
            <MenuItem
              onClick={close}
              key="profile"
              component="a"
              href={`${dataRepoURl}/uploads/new`}
            >
              New Upload
            </MenuItem>,
            <MenuItem
              onClick={close}
              key="my-account"
              component="a"
              href={`${dataRepoURl}/me/uploads`}
            >
              My Uploads
            </MenuItem>,
            <MenuItem
              onClick={close}
              key="logout"
              component="a"
              href={`${dataRepoURl}/me/requests`}
            >
              My Requests
            </MenuItem>,
          ]}
          id="data-repository-nav"
        />

        <SecondaryLink
          to="/computational-resources"
          aria-label="Computational Resources"
          onPage={onComp}
        >
          Computational Resources
        </SecondaryLink>
        <SecondaryLink to="/projects" aria-label="Projects" onPage={onProj}>
          Projects
        </SecondaryLink>
        <SecondaryLink
          to={docsUrl}
          aria-label="Help (opens in a new tab)"
          openNewTab
          rel="noopener noreferrer"
          onPage={false}
        >
          Help
          <FontAwesomeIcon
            icon={faArrowUpRightFromSquare}
            style={{ marginLeft: '0.4em', fontSize: '0.75em' }}
            aria-hidden="true"
          />
        </SecondaryLink>
      </SecondaryNav>
    </Root>
  );
}
export default observer(Header);